import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import DataTable from 'components/utils/DataTable';
import Info from 'components/utils/Alert/Info';
import { Paper, Button, FormControl, Select, MenuItem, Grid, Typography, Tooltip } from '@mui/material';
import OnlineOrderService from 'services/OnlineOrderService';
import styles from './styles.module.css';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import CustomTablePagination from 'components/utils/Pagination/CustomTablePagination';
import OnlineOrderDetails from './OnlineOrderDetails';
import SyncIcon from '@mui/icons-material/Sync';
import { useLocation, useHistory } from 'react-router-dom';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { Tabs, Tab } from '@mui/material';
import OnlinePaymentDetails from './OnlinePaymentDetails';
import { useTranslation } from 'react-i18next';

const PAGE_KEY = 'ONLINE_ORDERS';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function OnlineOrders() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const history = useHistory();
  const query = useQuery();

  const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange(PAGE_KEY);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [orders, setOrders] = useState([]);
  const [orderCount, setOrdersCount] = useState(0);

  const [payments, setPayments] = useState([]);
  const [paymentsCount, setPaymentsCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterFromDate, setFilterFromDate] = useState(dynamicStartDate);
  const [filterToDate, setFilterToDate] = useState(dynamicEndDate);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(false);
  const [prevFilterFromDate, setPrevFilterFromDate] = useState(dynamicStartDate);
  const [prevFilterToDate, setPrevFilterToDate] = useState(dynamicEndDate);
  const [activeTab, setActiveTab] = useState(0);
  const [orderStatus, setOrderStatus] = useState('PENDING');
  const [paymentStatus, setPaymentStatus] = useState('ALL');
  const [selectedPayment, setSelectedPayment] = useState(null);

  const openOrderDetails = (order) => {
    if (order?.id != null) {
      setSelectedOrder(order);
      history.replace({ search: `?order=${order.id}` });
    }
  };
  const closeOrderDetails = () => {
    setSelectedOrder(null);
    history.replace({ search: '' });
  };

  const openPaymentDetails = (item) => {
    if (item?.id != null) {
      setSelectedPayment(item);
      history.replace({ search: `?payment=${item.id}` });
    }
  };
  const closePaymentDetails = () => {
    setSelectedPayment(null);
    history.replace({ search: '' });
  };

  const fetchOnlineOrders = useCallback(
    async ({ _offset = 0, _limit = 50 } = {}) => {
      setLoading(true);
      try {
        const params = {
          offset: _offset,
          limit: _limit,
          filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(filterFromDate),
          filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(filterToDate),
        };
        if (orderStatus) {
          params.statuses = orderStatus;
        }
        const res = await OnlineOrderService.getOnlineOrders(params);
        setOrders(res.data);
        setOrdersCount(res.count);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      } finally {
        setLoading(false);
      }
    },
    [filterFromDate, filterToDate, orderStatus, postAlertMessage]
  );

  const fetchOnlinePayments = useCallback(
    async ({ _offset = 0, _limit = 50 } = {}) => {
      setLoading(true);
      try {
        const params = {
          offset: _offset,
          limit: _limit,
          filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(filterFromDate),
          filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(filterToDate),
        };
        if (paymentStatus) {
          params.statuses = paymentStatus;
        }
        const res = await OnlineOrderService.getCustomerPayments(params);
        setPayments(res.data);
        setPaymentsCount(res.count);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      } finally {
        setLoading(false);
      }
    },
    [filterFromDate, filterToDate, paymentStatus, postAlertMessage]
  );

  useEffect(() => {
    const orderParam = query.get('order');
    if (orderParam && orders.length > 0) {
      const found = orders.find((o) => String(o.id) === orderParam);
      if (found) {
        setSelectedOrder(found);
      }
    }
  }, [query, orders]);

  useEffect(() => {
    const paymentParam = query.get('payment');
    if (paymentParam && payments.length > 0) {
      const found = payments.find((p) => String(p.id) === paymentParam);
      if (found) {
        setSelectedPayment(found);
      }
    }
  }, [query, payments]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchOnlineOrders({ _offset: 0, _limit: 50 });
    } else {
      fetchOnlinePayments({ _offset: 0, _limit: 50 });
    }
    //eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    if (
      filterFromDate.getTime() !== prevFilterFromDate.getTime() ||
      filterToDate.getTime() !== prevFilterToDate.getTime()
    ) {
      setIsShowGenerateButton(true);
    }
  }, [filterFromDate, filterToDate, prevFilterFromDate, prevFilterToDate]);

  const headerData = [
    { label: tCommon('Order ID'), id: 'id', type: 'text' },
    {
      label: tCommon('Customer'),
      id: 'customer',
      type: 'callback',
      viewRender: (item) => item.customer?.name || '-',
    },
    { label: tCommon('Status'), id: 'status', type: 'text' },
    {
      label: tCommon('Date'),
      id: 'created_at_date',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.convertDateToIsoDMY(item.created_at),
    },
    {
      label: tCommon('Time'),
      id: 'created_at_time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.convertTo12HourTime(item.created_at),
    },
    {
      label: tCommon('Actions'),
      id: 'action',
      type: 'callback',
      viewRender: (item) => (
        <Tooltip title="View Details">
          <RemoveRedEyeIcon className={styles.iconBtn} onClick={() => openOrderDetails(item)} />
        </Tooltip>
      ),
    },
  ];

  const onlinePaymentsHeader = [
    {
      label: tCommon('Payment ID'),
      id: 'payment_id',
      type: 'callback',
      viewRender: (item) => (item.id != null ? item.id : '-'),
    },
    {
      label: tCommon('Customer'),
      id: 'customer',
      type: 'callback',
      viewRender: (item) => item.customer?.name || '-',
    },
    {
      label: tCommon('Amount'),
      id: 'amount',
      type: 'callback',
      viewRender: (item) => (item.amount != null ? item.amount : '-'),
    },
    {
      label: tCommon('Status'),
      id: 'status',
      type: 'callback',
      viewRender: (item) => item.status || '-',
    },
    {
      label: tCommon('Date'),
      id: 'initiated_at_date',
      type: 'callback',
      viewRender: (item) => (item.initiated_at ? DateTimeHelpers.convertDateToIsoDMY(item.initiated_at) : '-'),
    },
    {
      label: tCommon('Time'),
      id: 'initiated_at_time',
      type: 'callback',
      viewRender: (item) => (item.initiated_at ? DateTimeHelpers.convertTo12HourTime(item.initiated_at) : '-'),
    },
    {
      label: tCommon('Actions'),
      id: 'action',
      type: 'callback',
      viewRender: (item) => (
        <Tooltip title="View Details">
          <RemoveRedEyeIcon className={styles.iconBtn} onClick={() => openPaymentDetails(item)} />
        </Tooltip>
      ),
    },
  ];

  const handleRefresh = () => {
    if (activeTab === 0) {
      fetchOnlineOrders({ _offset: 0, _limit: 50 });
    } else {
      fetchOnlinePayments({ _offset: 0, _limit: 50 });
    }
  };

  return (
    <div className={styles.contentWrapper} style={{ padding: '10px' }}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Online Orders')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
        <div className={styles.refBtn} onClick={handleRefresh}>
          <Tooltip title={tCommon('Refresh Table')}>
            <SyncIcon />
          </Tooltip>
        </div>
      </div>

      <div className={styles.changeable}>
        <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
          <Grid container spacing={1} alignItems="flex-end">
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                {activeTab === 0
                  ? tOperations('Online Order for {{from}} to {{to}}', {
                      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                      to: DateTimeHelpers.convertDateToDMY(filterToDate),
                    })
                  : tOperations('Online Payment for {{from}} to {{to}}', {
                      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                      to: DateTimeHelpers.convertDateToDMY(filterToDate),
                    })}
              </Typography>
            </Grid>

            {/* Date Range */}
            <Grid item xs={12} md={3}>
              <DateTimeRange
                setFilterFromDate={setFilterFromDate}
                setFilterToDate={setFilterToDate}
                storageKey={PAGE_KEY}
              />
            </Grid>

            {/* Order Status (Tab 0) */}
            {activeTab === 0 && (
              <Grid item xs={12} md={3}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {tOperations('Order Status')}
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={orderStatus}
                    onChange={(e) => {
                      setOrderStatus(e.target.value);
                      setIsShowGenerateButton(true);
                    }}
                  >
                    <MenuItem value="PENDING">{tCommon('Pending')}</MenuItem>
                    <MenuItem value="CANCELLED">{tCommon('Cancelled')}</MenuItem>
                    <MenuItem value="REFUNDED">{tCommon('Refund')}</MenuItem>
                    <MenuItem value="all">{tCommon('All')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Payment Status (Tab 1) */}
            {activeTab === 1 && (
              <Grid item xs={12} md={3}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {tOperations('Payment Status')}
                </Typography>

                <FormControl fullWidth size="small">
                  <Select
                    value={paymentStatus}
                    onChange={(e) => {
                      setPaymentStatus(e.target.value);
                      setIsShowGenerateButton(true);
                    }}
                  >
                    <MenuItem value="ALL">{tCommon('All')}</MenuItem>
                    <MenuItem value="PENDING">{tCommon('Pending')}</MenuItem>
                    <MenuItem value="FAILED">{tCommon('Failed')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Generate Button */}
            <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {tCommon('Generate Report')}
              </Typography>

              <Button
                variant="contained"
                onClick={() => {
                  if (activeTab === 0) {
                    fetchOnlineOrders({ _offset: 0, _limit: 50 });
                  } else {
                    fetchOnlinePayments({ _offset: 0, _limit: 50 });
                  }
                  setPrevFilterFromDate(filterFromDate);
                  setPrevFilterToDate(filterToDate);
                  setIsShowGenerateButton(false);
                }}
                disabled={!isShowGenerateButton}
                startIcon={<SyncIcon />}
                sx={{
                  backgroundColor: '#00a65a',
                  color: '#fff',
                  height: 40,
                  minWidth: 160,
                  '&:hover': {
                    backgroundColor: '#00a65a',
                  },

                  '&.Mui-disabled': {
                    backgroundColor: '#00a65a',
                    color: '#fff',
                    opacity: 0.6,
                  },
                }}
              >
                {tCommon('Generate')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </div>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        className={styles.tab}
      >
        <Tab label="Online Orders" />
        <Tab label="Online Payments" />
      </Tabs>

      {activeTab === 0 ? (
        orders.length > 0 || loading ? (
          <Paper className={styles.tableWrapper}>
            <DataTable key="orders" columns={headerData} rows={orders || []} isLoading={loading} deactiveRow={false} />
            <CustomTablePagination
              storageKey={PAGE_KEY + '_ORDERS'}
              count={orderCount}
              onFetch={fetchOnlineOrders}
              defaults={{ _offset: 0, _limit: 50 }}
              rowsPerPageOptions={[50, 100]}
            />
          </Paper>
        ) : (
          !loading && (
            <Info
              title={tOperations('No orders found')}
              content={tOperations(
                'You have no orders to list with current filter configuration. Please clear the filters or create a order'
              )}
            />
          )
        )
      ) : payments.length > 0 || loading ? (
        <Paper className={styles.tableWrapper}>
          <DataTable
            key="payments"
            columns={onlinePaymentsHeader}
            rows={payments || []}
            isLoading={loading}
            deactiveRow={false}
          />
          <CustomTablePagination
            storageKey={PAGE_KEY + '_PAYMENTS'}
            count={paymentsCount}
            onFetch={fetchOnlinePayments}
            defaults={{ _offset: 0, _limit: 50 }}
            rowsPerPageOptions={[50, 100]}
          />
        </Paper>
      ) : (
        !loading && (
          <Info
            title={tOperations('No payments found')}
            content={tOperations(
              'You have no payments to list with current filter configuration. Please clear the filters or create a payment'
            )}
          />
        )
      )}

      {selectedOrder && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <OnlineOrderDetails
              order={selectedOrder}
              onClose={closeOrderDetails}
              onCloseWithRealod={() => {
                history.replace({
                  pathname: history.location.pathname,
                });
                handleRefresh();
                closeOrderDetails();
              }}
            />
          </div>
        </div>
      )}

      {selectedPayment && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <OnlinePaymentDetails payment={selectedPayment} onClose={closePaymentDetails} />
          </div>
        </div>
      )}
    </div>
  );
}

export default withConsoleBase(OnlineOrders);
